import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Spin, Steps, Typography } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { config } from '../../config';

const { Title, Paragraph, Text } = Typography;

const fmt = (iso) => {
    if (!iso) return 'unknown';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export function ExtensionPage() {
    const { extensionDownloadUrl, extensionVersionUrl } = config();
    const [version, setVersion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadVersion = () => {
        setLoading(true);
        setError(null);
        fetch(`${extensionVersionUrl}?t=${Date.now()}`, { cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(setVersion)
            .catch(() => setError("Couldn't read the published version — the extension may not have been published yet."))
            .finally(() => setLoading(false));
    };

    useEffect(loadVersion, [extensionVersionUrl]);

    return (
        <div style={{ padding: 24, maxWidth: 760 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>
                    Pipeline Chrome Extension
                </Title>
                <Button icon={<ReloadOutlined />} onClick={loadVersion} loading={loading}>
                    Refresh
                </Button>
            </Space>

            <Card style={{ marginBottom: 24 }}>
                <Spin spinning={loading}>
                    {error && <Alert type="warning" showIcon message={error} style={{ marginBottom: 16 }} />}
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Text type="secondary">Last updated</Text>
                            <div>
                                <Text strong style={{ fontSize: 16 }}>{fmt(version?.builtAt)}</Text>
                                {version?.version && (
                                    <Text type="secondary" style={{ marginLeft: 8 }}>
                                        v{version.version}
                                        {version.commit ? ` · ${String(version.commit).slice(0, 7)}` : ''}
                                    </Text>
                                )}
                            </div>
                        </div>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            href={extensionDownloadUrl}
                            disabled={!!error}
                        >
                            Download extension (.zip)
                        </Button>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            The extension shows its own build time in the popup footer — if it's older than the
                            date above, download and reinstall the latest.
                        </Text>
                    </Space>
                </Spin>
            </Card>

            <Title level={4}>Install / update</Title>
            <Steps
                direction="vertical"
                size="small"
                current={-1}
                items={[
                    {
                        title: 'Download & unzip',
                        description: 'Download the .zip above and extract it to a folder you’ll keep (the loaded extension reads from this folder).',
                    },
                    {
                        title: 'Open Chrome extensions',
                        description: 'Go to chrome://extensions in Chrome.',
                    },
                    {
                        title: 'Enable Developer mode',
                        description: 'Toggle "Developer mode" on (top-right of the extensions page).',
                    },
                    {
                        title: 'Load unpacked',
                        description: 'Click "Load unpacked" and select the unzipped folder.',
                    },
                    {
                        title: 'Pin & sign in',
                        description: 'Pin the extension, open it on a retailer product page, and sign in with your Google admin account.',
                    },
                    {
                        title: 'Updating later',
                        description: 'Re-download, replace the folder contents with the new files, then click the refresh ⟳ icon on the extension card in chrome://extensions.',
                    },
                ]}
            />
        </div>
    );
}
